import React from "react";
import { Link } from "react-router-dom";
import EigooMark from "./EigooMark";
import SovereignEye from "./SovereignEye";
import { BoardLink } from "../lib/boardLinks";
import MagicLinkInline from "./MagicLinkInline";
import SocialSignInButtons from "./SocialSignInButtons";
import {
    Lock,
    Eye,
    EyeOff,
    ArrowRight,
    Shield,
    Crown,
    Anchor,
    UserPlus,
    KeyRound,
    Wand2,
    Activity,
} from "lucide-react";

/**
 * GhostBridge — public/anonymous view of the Master Command Bridge.
 *
 * Director directive 2026.02 · Global Privacy Perimeter:
 * Unauthenticated visitors are NEVER allowed to see real fleet data,
 * revenue figures, partner identities, or operational signals. Instead
 * this component renders a faithful TEMPLATE / DEMO of the Bridge with:
 *
 *   • All values clearly tagged "TEMPLATE" / "PREVIEW" / "DEMO"
 *   • A diagonal "GHOST VIEW" watermark across the panels
 *   • Three prominent Sign-In / Register / Magic-Link CTAs
 *   • The same visual hierarchy as the real Bridge so visitors know
 *     what they unlock by registering
 *
 * Mounted by the Bridge route when `useAuth().user` is falsy.
 */

const GHOST_SIGNALS = [
    { id: "g1", label: "Boarding Velocity", value: "$ — — — k", trend: "TEMPLATE", color: "hsl(45 85% 68%)" },
    { id: "g2", label: "Foundry Strikes (24h)", value: "—", trend: "TEMPLATE", color: "hsl(153 60% 60%)" },
    { id: "g3", label: "CyberKids Tithe", value: "$ — — —", trend: "TEMPLATE", color: "hsl(40 75% 55%)" },
    { id: "g4", label: "Active Operatives", value: "—", trend: "TEMPLATE", color: "hsl(45 85% 68%)" },
];

const GHOST_VESSELS = [
    { id: "v1", name: "Trade Desk · NC", status: "TEMPLATE", mass: "$██,███" },
    { id: "v2", name: "Sports Signal · CA", status: "TEMPLATE", mass: "$██,███" },
    { id: "v3", name: "TechOps · TX", status: "TEMPLATE", mass: "$██,███" },
    { id: "v4", name: "Foundry · Remote", status: "TEMPLATE", mass: "$██,███" },
];

export default function GhostBridge() {
    return (
        <div className="min-h-screen bg-background grain-overlay relative" data-testid="ghost-bridge">
            <div aria-hidden="true" className="starfield fixed inset-0 pointer-events-none opacity-[0.18] z-0" />

            {/* Header */}
            <header className="relative z-10 max-w-[1500px] mx-auto flex items-center justify-between px-6 md:px-10 py-6">
                <EigooMark size="lg" sub="Master Bridge · Ghost View" testid="ghost-mark" />
                <div className="flex items-center gap-3">
                    <Link
                        to="/login"
                        data-testid="ghost-cta-login-header"
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                    >
                        <KeyRound size={14} /> Sign in
                    </Link>
                    <BoardLink
                        data-testid="ghost-cta-register-header"
                        className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-[hsl(40_50%_35%/0.5)] text-foreground hover:bg-[hsl(151_55%_15%)] transition-colors"
                    >
                        <UserPlus size={14} /> Board the fleet
                    </BoardLink>
                </div>
            </header>

            {/* Privacy banner */}
            <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10">
                <div
                    className="glass rounded-2xl px-5 py-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-3 fade-up"
                    style={{ borderColor: "hsl(40 70% 45% / 0.55)", background: "hsl(40 70% 22% / 0.4)" }}
                    data-testid="ghost-privacy-banner"
                >
                    <div className="flex items-center gap-2 shrink-0">
                        <EyeOff size={18} className="text-[hsl(45_85%_68%)]" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_70%)]">Sovereign Privacy Perimeter</div>
                            <div className="text-xs font-semibold text-[hsl(45_85%_78%)]">
                                Real fleet data is sealed. You are viewing a Template.
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 min-w-[200px] text-xs text-muted-foreground">
                        Sign in to see real Boarding Velocity, Foundry deliveries, CyberKids tithe, and your own residual ledger.
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <Link
                            to="/login"
                            data-testid="ghost-cta-login-banner"
                            className="text-xs uppercase tracking-[0.25em] text-[hsl(45_85%_75%)] hover:text-[hsl(45_95%_85%)] underline underline-offset-4"
                        >
                            Sign in
                        </Link>
                        <span className="text-muted-foreground">·</span>
                        <Link
                            to="/login?magic=1"
                            data-testid="ghost-cta-magic-banner"
                            className="text-xs uppercase tracking-[0.25em] text-[hsl(45_85%_75%)] hover:text-[hsl(45_95%_85%)] underline underline-offset-4 inline-flex items-center gap-1"
                        >
                            <Wand2 size={11} /> Magic link
                        </Link>
                    </div>
                </div>
            </div>

            {/* Hero */}
            <section className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 pb-10">
                <div className="fade-up max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(40_50%_40%/0.4)] text-[10px] uppercase tracking-[0.3em] text-[hsl(45_60%_70%)] mb-6">
                        <Lock size={11} /> Restricted · authentication required
                    </div>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                        The Master Command Bridge —
                        <span className="block gold-gradient-text mt-2">behind the seal.</span>
                    </h1>
                    <p className="mt-8 text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed">
                        Real revenue · real deliveries · real operatives — visible only to vetted Fleet
                        Commanders. Board the Fleet to claim a territory, deploy ad-capital, and
                        collect your 70% residual under the Sovereign Covenant.
                    </p>
                </div>

                {/* Mock stat grid — clearly tagged TEMPLATE */}
                <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 relative" data-testid="ghost-stat-grid">                    {/* Diagonal watermark — visual reinforcement that this is NOT real data */}
                    <div
                        aria-hidden="true"
                        className="absolute inset-0 pointer-events-none flex items-center justify-center select-none"
                        style={{
                            transform: "rotate(-12deg)",
                            opacity: 0.08,
                            fontSize: "clamp(80px, 14vw, 220px)",
                            fontWeight: 900,
                            color: "hsl(45 85% 68%)",
                            letterSpacing: "0.4em",
                            zIndex: 1,
                        }}
                    >
                        GHOST
                    </div>
                    {GHOST_SIGNALS.map((s) => (
                        <div
                            key={s.id}
                            className="glass rounded-xl p-5 relative"
                            style={{ zIndex: 2 }}
                            data-testid={`ghost-signal-${s.id}`}
                        >
                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{s.label}</div>
                            <div className="font-heading text-3xl mt-2 tracking-tight" style={{ color: s.color }}>
                                {s.value}
                            </div>
                            <div className="mt-3 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.3em] px-2 py-0.5 rounded-full bg-[hsl(40_70%_25%)] text-[hsl(45_85%_75%)] border border-[hsl(45_85%_60%/0.4)]">
                                <Activity size={9} /> {s.trend}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Sovereign Eye · public threat field */}
                <div className="mt-10">
                    <SovereignEye mode="public" height={480} />
                </div>

                {/* Vessel preview row */}
                <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 glass rounded-2xl p-6 relative" data-testid="ghost-vessels">
                        <div className="flex items-baseline justify-between mb-4">
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Fleet Status</div>
                                <h2 className="font-heading text-2xl mt-1">Active Sites</h2>
                            </div>
                            <span className="text-[10px] uppercase tracking-[0.25em] text-[hsl(45_85%_68%)]">TEMPLATE</span>
                        </div>
                        <ul className="space-y-2">
                            {GHOST_VESSELS.map((v) => (
                                <li
                                    key={v.id}
                                    className="flex items-center justify-between p-3 rounded-lg border border-[hsl(40_40%_25%/0.3)]"
                                    data-testid={`ghost-vessel-${v.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Anchor size={14} className="text-[hsl(45_85%_68%)]" />
                                        <span className="text-sm font-medium">{v.name}</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono text-sm text-muted-foreground">{v.mass}</span>
                                        <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[hsl(151_40%_18%)] text-muted-foreground">
                                            {v.status}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Conversion panel — the prominent registration CTA */}
                    <div className="glass rounded-2xl p-6 border-[hsl(45_85%_60%/0.5)]" data-testid="ghost-cta-panel">
                        <div className="flex items-center gap-2 mb-4">
                            <Crown size={20} className="text-[hsl(45_85%_68%)]" />
                            <div className="font-heading text-xl">Board the Fleet</div>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                            Register your platform. Claim a state. Deploy ad-capital. Collect 60% residual under
                            a transparent covenant — every month, in perpetuity.
                        </p>
                        <Link
                            to="/register"
                            data-testid="ghost-cta-register-primary"
                            className="mt-5 group inline-flex items-center gap-2 w-full justify-center px-6 py-3 rounded-full bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                        >
                            Register a Sovereign account <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                        </Link>
                        <Link
                            to="/login"
                            data-testid="ghost-cta-login-panel"
                            className="mt-3 inline-flex items-center gap-2 w-full justify-center px-6 py-3 rounded-full border border-[hsl(40_50%_35%/0.5)] text-foreground hover:bg-[hsl(151_55%_15%)] transition-colors"
                        >
                            <KeyRound size={14} /> Already aboard · Sign in
                        </Link>

                        {/* Secondary fallback · Magic-Link email login.
                            Zero-friction recovery path when password flow
                            fails (cookie loop, lost password, MFA token
                            misplaced). Director directive 2026.02 · Strike #18. */}
                        <div
                            data-testid="ghost-magic-fallback"
                            className="mt-5 pt-4 border-t border-[hsl(40_40%_30%/0.18)]"
                        >
                            <MagicLinkInline
                                accent="gold"
                                label="Or · one-tap magic link"
                                placeholder="commander@yourdomain.com"
                                testidPrefix="ghost-magic"
                            />
                        </div>

                        {/* Social sign-in · Strike #19 · Google live,
                            Apple queued pending Apple Developer creds. */}
                        <div className="mt-5 pt-4 border-t border-[hsl(40_40%_30%/0.18)]">
                            <SocialSignInButtons testidPrefix="ghost-social" />
                        </div>

                        <div className="mt-5 pt-4 border-t border-[hsl(40_40%_30%/0.18)] flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                            <Shield size={11} className="text-[hsl(45_85%_68%)]" /> Vetted by the Director · LinkedIn required
                        </div>
                    </div>
                </div>
            </section>

            <footer className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-10 py-10 border-t border-[hsl(40_40%_30%/0.15)] text-xs text-muted-foreground flex flex-wrap items-center justify-between gap-4">
                <div>© EIGOO Sovereign Fleet · Privacy by default.</div>
                <div className="flex items-center gap-4">
                    <Link to="/login" className="hover:text-foreground">Sign in</Link>
                    <span>·</span>
                    <BoardLink className="hover:text-foreground">Register</BoardLink>
                    <span>·</span>
                    <Link to="/" className="hover:text-foreground">Public hub</Link>
                </div>
            </footer>
        </div>
    );
}
